import React from 'react';
import {
  ArrowsClockwise,
  Buildings,
  CircleNotch,
  CurrencyDollar,
  ListChecks,
  Plus,
} from '@phosphor-icons/react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import AddRateDialog from './payer-contracts/AddRateDialog';
import ContractCard from './payer-contracts/ContractCard';
import NewContractDialog from './payer-contracts/NewContractDialog';
import { PAYER_LABELS, PAYER_ORDER, usePayerContracts } from './payer-contracts/usePayerContracts';

const EmptyState = ({ seeding, onSeed }) => (
  <Card className="border-slate-200">
    <CardContent className="text-center py-16">
      <Buildings size={48} className="mx-auto mb-4 text-slate-300" />
      <h3 className="text-lg font-semibold text-slate-700 mb-2">No Payer Contracts Yet</h3>
      <p className="text-slate-500 mb-4">
        Click "Load Ohio Defaults" to seed all payers with standard Ohio Medicaid rates
      </p>
      <Button
        onClick={onSeed}
        disabled={seeding}
        className="bg-indigo-900 hover:bg-indigo-800"
        data-testid="seed-btn-empty"
      >
        {seeding ? (
          <CircleNotch size={16} className="mr-2 animate-spin" />
        ) : (
          <ListChecks size={16} className="mr-2" />
        )}
        Load Ohio Default Rates
      </Button>
    </CardContent>
  </Card>
);

const PayerContractsPage = () => {
  const c = usePayerContracts();

  if (c.loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payer-contracts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Payer Contracts & Rates
          </h1>
          <p className="text-slate-500">Manage HCPCS billing codes and rates per payer contract</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!c.hasContracts && (
            <Button
              variant="outline"
              onClick={c.seedDefaults}
              disabled={c.seeding}
              data-testid="seed-defaults-btn"
            >
              {c.seeding ? (
                <CircleNotch size={16} className="mr-1 animate-spin" />
              ) : (
                <ArrowsClockwise size={16} className="mr-1" />
              )}
              Load Ohio Defaults
            </Button>
          )}
          <Button
            onClick={() => c.setShowNewContract(true)}
            className="bg-indigo-900 hover:bg-indigo-800"
            data-testid="add-contract-btn"
          >
            <Plus size={18} className="mr-2" /> New Contract
          </Button>
        </div>
      </div>

      {/* Body */}
      {!c.hasContracts ? (
        <EmptyState seeding={c.seeding} onSeed={c.seedDefaults} />
      ) : (
        <Accordion
          type="multiple"
          defaultValue={PAYER_ORDER.filter((p) => c.contractsByPayer[p]?.length)}
          className="space-y-3"
        >
          {PAYER_ORDER.map((payerType) => {
            const contracts = c.contractsByPayer[payerType];
            if (!contracts || contracts.length === 0) return null;
            const totalRates = contracts.reduce((sum, x) => sum + (x.rates?.length || 0), 0);
            const activeCount = contracts.filter((x) => x.status === 'Active').length;
            return (
              <AccordionItem
                key={payerType}
                value={payerType}
                className="border rounded-lg bg-white shadow-sm"
              >
                <AccordionTrigger
                  className="px-5 py-4 hover:no-underline"
                  data-testid={`payer-${payerType}`}
                >
                  <div className="flex items-center gap-4 w-full mr-4">
                    <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                      <CurrencyDollar size={24} className="text-indigo-700" />
                    </div>
                    <div className="text-left flex-1">
                      <h3 className="text-lg font-semibold text-slate-900">
                        {PAYER_LABELS[payerType] || payerType}
                      </h3>
                      <p className="text-sm text-slate-500">
                        {contracts.length} contract{contracts.length > 1 ? 's' : ''} · {totalRates} billing codes · {activeCount} active
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {activeCount > 0 && (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                          {activeCount} Active
                        </Badge>
                      )}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-5 pb-5">
                  <div className="space-y-4">
                    {contracts.map((contract) => (
                      <ContractCard
                        key={contract.id}
                        contract={contract}
                        editingRate={c.editingRate}
                        setEditingRate={c.setEditingRate}
                        savingRate={c.savingRate}
                        onAddRate={c.setAddRateDialog}
                        onToggleStatus={c.toggleContractStatus}
                        onDeleteContract={c.deleteContract}
                        onStartEditRate={c.startEditRate}
                        onSaveRate={c.saveRate}
                        onDeleteRate={c.deleteRate}
                      />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      <NewContractDialog
        open={c.showNewContract}
        onOpenChange={c.setShowNewContract}
        newContractPayer={c.newContractPayer}
        setNewContractPayer={c.setNewContractPayer}
        newContractForm={c.newContractForm}
        setNewContractForm={c.setNewContractForm}
        saving={c.savingContract}
        onSave={c.createContract}
      />

      <AddRateDialog
        open={!!c.addRateDialog}
        onOpenChange={() => c.setAddRateDialog(null)}
        newRate={c.newRate}
        setNewRate={c.setNewRate}
        onSave={c.addRate}
      />
    </div>
  );
};

export default PayerContractsPage;
